/** Entity fields as edited in the entity editor: rows with a stable identity and their original keys. */
import type { Field as FieldDef } from "../api/types.gen.ts";

let uids = 0;

/** `[old, new]` for fields whose key changed since loading. */
const renamed = (rows: readonly Row[]): [string, string][] =>
  rows.flatMap((r) => (r.orig && r.orig !== r.key.trim() ? [[r.orig, r.key.trim()]] : []));

/** The types whose (top-level) fields can be unique, compared by key; ref fields' uniqueness is their own. */
export const UNIQUE_TYPES: ReadonlySet<FieldDef["type"]> = new Set([
  "text",
  "email",
  "phone",
  "url",
  "number",
  "percentage",
]);

/** The formats whose values read in a region of the field's own: plates' and VINs' countries, IEs' states. */
const REGIONAL_FORMATS: ReadonlySet<string> = new Set(["license_plate", "vin", "br_ie"]);

/** The types with settings of their own in the entity editor (a ref field's include its link fields). */
const HAS_SETTINGS: ReadonlySet<FieldDef["type"]> = new Set([
  "select",
  "ref",
  "money",
  "datetime",
  "text",
  "phone",
  "quantity",
  "location",
]);

/** Whether a field of `type` has settings (a link field's: not uniqueness). */
export const hasSettings = (type: FieldDef["type"], link: boolean): boolean =>
  HAS_SETTINGS.has(type) || (!link && UNIQUE_TYPES.has(type));

/** The attributes only some types have, as the server expects them. */
const TYPE_ATTRS: Partial<Record<FieldDef["type"], (r: Row) => Partial<FieldDef>>> = {
  text: (r) =>
    r.format
      ? { format: r.format, ...(REGIONAL_FORMATS.has(r.format) && r.region && { region: r.region }) }
      : { ...(r.pattern && { pattern: r.pattern }), ...(r.mask && { mask: r.mask }) },
  phone: (r) => ({ ...(r.region && { region: r.region }), ...(r.strict && { strict: true }) }),
  quantity: (r) => (r.unit ? { unit: r.unit } : r.units?.length ? { units: r.units } : {}),
  select: (r) => {
    const options = r.options ?? [];
    // Labels of the options left, and not blank.
    const labels = Object.entries(r.labels ?? {}).flatMap(([o, l]): [string, string][] =>
      options.includes(o) && l.trim() ? [[o, l.trim()]] : [],
    );
    return { options, ...(labels.length > 0 && { labels: Object.fromEntries(labels) }) };
  },
  ref: (r) => ({
    target: r.target || null,
    unique: r.unique ?? false,
    ...(r.ownership && { ownership: r.ownership }),
    ...(r.fields?.length && { fields: r.fields.map((f) => toField(f, true)) }),
  }),
  money: (r) => (r.currency ? { currency: r.currency, ...(r.fixed_currency && { fixed_currency: true }) } : {}),
  datetime: (r) => (r.time_zone ? { time_zone: r.time_zone } : {}),
};

/** A field being edited; `orig` is its key as loaded (to detect renames), `uid` its stable identity. */
export interface Row extends FieldDef {
  uid: number;
  orig?: string;
  /** A ref field's link fields. */
  fields?: Row[];
}

export const rowsOf = (fields: readonly FieldDef[]): Row[] =>
  fields.map(({ fields: links, ...f }) => ({
    ...f,
    uid: nextUid(),
    orig: f.key,
    ...(links && { fields: rowsOf(links) }),
  }));

/** After saving: the keys as saved become the originals. */
export const asSaved = (rows: readonly Row[]): Row[] =>
  rows.map((r) => ({ ...r, orig: r.key.trim(), ...(r.fields && { fields: asSaved(r.fields) }) }));

export const nextUid = () => {
  uids += 1;
  return uids;
};

/** The field as sent to the server: only the attributes its type uses (`link`: a ref field's link field). */
export function toField(r: Row, link = false): FieldDef {
  return {
    key: r.key.trim(),
    type: r.type,
    ...(r.label && { label: r.label }),
    ...(r.required && { required: true }),
    ...(r.many && { many: true }),
    ...(r.show_on_table && { show_on_table: true }),
    ...(!link && r.unique && UNIQUE_TYPES.has(r.type) && { unique: true }),
    ...TYPE_ATTRS[r.type]?.(r),
  };
}

/** `{old: new}` for renamed fields, and `{"ref.old": "ref.new"}` for renamed link fields (under the new ref key). */
export const renamesOf = (rows: readonly Row[]): Record<string, string> =>
  Object.fromEntries([
    ...renamed(rows),
    ...rows.flatMap((r) =>
      r.type === "ref" && r.orig
        ? renamed(r.fields ?? []).map(([a, b]): [string, string] => [`${r.key.trim()}.${a}`, `${r.key.trim()}.${b}`])
        : [],
    ),
  ]);
